import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { showToast, showError } from "../../constants/toasts";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

interface FormErrors {
  email?: string;
  password?: string;
}

const REMEMBER_ME_KEY = "rememberedCredentials";

interface RememberedCredentials {
  email: string;
  password: string;
}

const Login = () => {
  const navigate = useNavigate();
  const { login, isLoginLoading } = useAuth();
  const { token, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [rememberMe, setRememberMe] = useState(false);

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (token && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [token, isAuthenticated, navigate]);

  // Load remembered credentials on component mount
  useEffect(() => {
    const rememberedData = localStorage.getItem(REMEMBER_ME_KEY);
    if (rememberedData) {
      try {
        const credentials: RememberedCredentials = JSON.parse(rememberedData);
        if (credentials.email && credentials.password) {
          setFormData({
            email: credentials.email,
            password: credentials.password,
          });
          setRememberMe(true);
        }
      } catch (error) {
        // If parsing fails, clear the invalid data
        localStorage.removeItem(REMEMBER_ME_KEY);
      }
    }
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Invalid password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // Handle remember me checkbox change
  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);
    
    if (!isChecked) {
      // If unchecked, clear the stored credentials
      localStorage.removeItem(REMEMBER_ME_KEY);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showError("Please fix the errors in the form");
      return;
    }

    try {
      const response = await login({
        email: formData.email.trim(),
        password: formData.password,
      });

      if (response) {
        // Save credentials to localStorage if "Remember Me" is checked
        if (rememberMe) {
          const credentials: RememberedCredentials = {
            email: formData.email.trim(),
            password: formData.password,
          };
          localStorage.setItem(REMEMBER_ME_KEY, JSON.stringify(credentials));
        } else {
          // Clear stored credentials if "Remember Me" is unchecked
          localStorage.removeItem(REMEMBER_ME_KEY);
        }
        
        showToast("Login successful!");
        navigate("/dashboard", { replace: true });
      }
    } catch (error: any) {
      // Handle error response
      let errorMessage = "Login failed. Please check your credentials and try again.";
      
      // Handle PARSING_ERROR (when server returns HTML instead of JSON)
      if (error?.status === "PARSING_ERROR" || error?.error?.status === "PARSING_ERROR") {
        const originalStatus = error?.originalStatus || error?.error?.originalStatus;
        if (originalStatus === 404) {
          errorMessage = "Endpoint not found. Please check the API configuration.";
        } else {
          errorMessage = "Server returned an invalid response. Please check the API endpoint.";
        }
        console.error("PARSING_ERROR details:", error);
      } else {
        // Try to extract error message from various error formats
        errorMessage =
          error?.data?.message ||
          error?.data?.error ||
          error?.error?.data?.message ||
          error?.error?.data?.error ||
          error?.message ||
          errorMessage;
      }
      
      showError(errorMessage);
    }
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Login</h2>

        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <label htmlFor="email">Email Address</label>
            <TextField
              id="email"
              name="email"
              type="email"
              placeholder="Enter Email Address"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email || " "}
              FormHelperTextProps={{
                style: { margin: 0, minHeight: errors.email ? "auto" : "20px" }
              }}
              disabled={isLoginLoading}
            />
          </div>

          <div className="control_group">
            <label htmlFor="password">Password</label>
            <TextField
              id="password"
              name="password"
              placeholder="Enter Password"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password || " "}
              FormHelperTextProps={{
                style: { margin: 0, minHeight: errors.password ? "auto" : "20px" }
              }}
              disabled={isLoginLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      disabled={isLoginLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="control_group d_flex">
            <div className="remember_box">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    disabled={isLoginLoading}
                  />
                }
                name="remember"
                label="Remember me"
              />
            </div>
            <button
              type="button"
              className="anchor_link"
              onClick={() => navigate("/forgot-password")}
              disabled={isLoginLoading}
            >
              Forgot Password?
            </button>
          </div>

          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              variant="contained"
              disabled={isLoginLoading}
            >
              {isLoginLoading ? "Logging in..." : "Login"}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
